/**
 * A pílula de estado do sistema, na topbar.
 *
 * A sonda distingue três coisas que a tela precisa dizer com palavras diferentes: a API
 * responde e está pronta; a API responde e o **banco** não; a API não responde. Colapsar as
 * três em "offline" faria o operador ligar para a pessoa errada.
 *
 * `/saude/pronto` não exige autenticação e não passa pelo wrapper `api()` de propósito: ela
 * precisa funcionar justamente quando a sessão não funciona.
 */
import { useEffect, useState } from "react";

import type { RespostaPronto } from "../api/tipos";
import { repetirComAbaVisivel } from "./repetirComAbaVisivel";

export type EstadoDoSistema = {
  cor: "verde" | "amarelo" | "vermelho";
  rotulo: string;
};

const VERIFICANDO: EstadoDoSistema = { cor: "amarelo", rotulo: "Verificando" };
const ONLINE: EstadoDoSistema = { cor: "verde", rotulo: "Sistema online" };
const SEM_BANCO: EstadoDoSistema = {
  cor: "vermelho",
  rotulo: "Banco indisponível",
};
const OFFLINE: EstadoDoSistema = { cor: "vermelho", rotulo: "Offline" };

export const INTERVALO_MS = 10_000;

export async function conferirSaude(): Promise<EstadoDoSistema> {
  try {
    const resposta = await fetch("/saude/pronto");
    const corpo = (await resposta.json()) as RespostaPronto;
    if (corpo.pronto) return ONLINE;
    return corpo.banco
      ? { cor: "vermelho", rotulo: corpo.detalhe ?? "Indisponível" }
      : SEM_BANCO;
  } catch {
    return OFFLINE;
  }
}

export function useSaude(): EstadoDoSistema {
  const [estado, definirEstado] = useState<EstadoDoSistema>(VERIFICANDO);

  useEffect(() => {
    let vivo = true;
    const conferir = async () => {
      const proximo = await conferirSaude();
      if (vivo) definirEstado(proximo);
    };
    void conferir();
    const parar = repetirComAbaVisivel(() => void conferir(), INTERVALO_MS);
    return () => {
      vivo = false;
      parar();
    };
  }, []);

  return estado;
}
